import { useEventListener } from "@react-hookz/web";
import {
  ComponentProps,
  Dispatch,
  RefObject,
  SetStateAction,
  useRef,
} from "react";
import { isVideo } from "@utils/isVideo";
import { useIsOnScreenEffect } from "@hooks/useIsOnScreenEffect";

export type MediaItemType = {
  url: string | null;
  contentType: string | null;
} | null;

const buildImageUrl = (imageUrl: string, width?: number) => {
  if (!imageUrl.length) return "";

  const url = new URL(imageUrl);
  url.searchParams.set("fm", "webp");

  const scaledWidth = Math.min(width ?? window.innerWidth, window.innerWidth);
  const scaledHeight = window.innerHeight;

  if (width || scaledWidth > scaledHeight) {
    url.searchParams.set("w", String(scaledWidth));
  } else {
    url.searchParams.set("h", String(scaledHeight));
  }

  return url.toString();
};

export const MediaItem = ({
  item,
  width,
  setIsLoaded,
  onScreenDeterminationElementRef: externalOnScreenDeterminationElementRef,
  ...props
}: {
  item: MediaItemType;
  setIsLoaded?: Dispatch<SetStateAction<boolean>>;
  // uses "section" because sections are generic HTML elements
  // without unique props
  width?: number;
  onScreenDeterminationElementRef?: RefObject<Element>;
} & Omit<ComponentProps<"video"> & ComponentProps<"img">, "ref">) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEventListener(ref, "canplaythrough", () => {
    if (setIsLoaded) {
      setIsLoaded(true);
    }
  });

  const onScreenDeterminationElementRef =
    externalOnScreenDeterminationElementRef ?? ref;
  useIsOnScreenEffect(
    onScreenDeterminationElementRef,
    (isOnScreen) => {
      const element = ref.current;
      if (!(element instanceof HTMLVideoElement)) return;
      if (isOnScreen) element.play();
      else element.pause();
    },
    []
  );
  if (!item || !item.url) return null;

  const posterUrl = buildImageUrl(props.poster ?? "", width);

  if (isVideo(item)) {
    return (
      <video
        ref={ref}
        src={item.url}
        autoPlay
        muted
        loop
        playsInline
        {...props}
        poster={posterUrl}
      />
    );
  }

  const url = buildImageUrl(item.url, width);

  return <img src={url} {...props} />;
};
